import type {
  ExternalRegisterImportAdapter,
  ImportCommitResponse,
  ImportParseResponse,
  ImportValidateResponse,
} from "./externalRegisterImportTypes";

type ClientResponse<T> = {
  data?: T;
};

type ExternalRegisterImportClient = {
  post: (url: string, payload?: unknown, options?: unknown) => Promise<unknown>;
};

const getDefaultClient = async (): Promise<ExternalRegisterImportClient> => {
  const globalModule = await import("@/global");
  return globalModule.default.client as ExternalRegisterImportClient;
};

const unwrapResponse = <T>(response: unknown, fallback: T): T => {
  return ((response as ClientResponse<T>)?.data ?? fallback) as T;
};

export const createExternalRegisterImportLiveAdapter = (
  client?: ExternalRegisterImportClient,
): ExternalRegisterImportAdapter => ({
  async parse(request) {
    const targetClient = client ?? await getDefaultClient();
    const response = await targetClient.post("patients/custom/external-register/import/parse", request);
    return unwrapResponse<ImportParseResponse>(response, {
      clientImportId: request.clientImportId,
      sourceFingerprint: "",
      status: "parsed",
      rowCount: 0,
      columnCount: 0,
      hasHeaderGuess: false,
      columns: [],
      sampleRows: [],
      parseErrors: [],
    });
  },

  async validate(request) {
    const targetClient = client ?? await getDefaultClient();
    const response = await targetClient.post("patients/custom/external-register/import/validate", request);
    return unwrapResponse<ImportValidateResponse>(response, {
      clientImportId: request.clientImportId,
      sourceFingerprint: "",
      status: "failed",
      totalRows: 0,
      validRows: 0,
      errorRows: 0,
      needsReviewRows: 0,
      readyRows: 0,
      createsNewPatientRows: 0,
      updateOrReactivationRows: 0,
      preview: [],
    });
  },

  async commit(request) {
    const targetClient = client ?? await getDefaultClient();
    const response = await targetClient.post("patients/custom/external-register/import/commit", request);
    const commitResponse = unwrapResponse<ImportCommitResponse>(response, {
      clientImportId: request.clientImportId,
      sourceFingerprint: request.sourceFingerprint,
      status: "failed",
      batchSize: 0,
      totalBatches: 0,
      currentBatch: 0,
      totalRecords: 0,
      successfullyImported: 0,
      errors: 1,
      skippedOrExcluded: 0,
      retryableRows: 0,
      matchedOnUmrn: 0,
      matchedOnDemographics: 0,
      newPatientsCreated: 0,
      updatedExistingEntries: 0,
      batches: [],
      rows: [],
    });
    return [commitResponse];
  },
});

export const externalRegisterImportLiveAdapter = createExternalRegisterImportLiveAdapter();
